/**
 * Number, duration and clock formatting.
 *
 * Two rules the whole dashboard depends on:
 *  1. One locale, decided here. Mixed separators in a dense grid read as noise.
 *  2. `Intl` formatters are built once. At a 2s tick across ~80 numbers, building
 *     them per render is measurable.
 *
 * Clocks render in UTC on purpose: the platform team correlates this screen with
 * server logs and with tenants in other time zones. Local time would be a
 * silent source of off-by-hours mistakes, so UTC is shown and labelled.
 */

export const LOCALE = 'pt-BR'
export const CLOCK_ZONE = 'UTC'

/** Rendered wherever a value is genuinely unknown, never as a stand-in for 0. */
export const NO_VALUE = '—'

const int = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 })
const oneDecimal = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const twoDecimal = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const compact = new Intl.NumberFormat(LOCALE, {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
})
const clock = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: CLOCK_ZONE,
})
const clockShort = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: CLOCK_ZONE,
})
const dayClock = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: CLOCK_ZONE,
})

const dayMonth = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  timeZone: CLOCK_ZONE,
})

const isNum = (n: number | null | undefined): n is number =>
  typeof n === 'number' && Number.isFinite(n)

export function formatInt(n: number | null | undefined): string {
  return isNum(n) ? int.format(n) : NO_VALUE
}

export function formatDecimal(n: number | null | undefined, digits: 1 | 2 = 1): string {
  if (!isNum(n)) return NO_VALUE
  return digits === 1 ? oneDecimal.format(n) : twoDecimal.format(n)
}

/** Axis and badge labels only. A headline metric always shows every digit. */
export function formatCompact(n: number | null | undefined): string {
  return isNum(n) ? compact.format(n) : NO_VALUE
}

export function formatPct(n: number | null | undefined, digits: 0 | 1 = 1): string {
  if (!isNum(n)) return NO_VALUE
  return `${digits === 0 ? int.format(n) : oneDecimal.format(n)}%`
}

/** Sub-millisecond precision is noise; above a second, ms is unreadable. */
export function formatMs(ms: number | null | undefined): string {
  if (!isNum(ms)) return NO_VALUE
  if (ms >= 1000) return `${oneDecimal.format(ms / 1000)} s`
  if (ms < 10) return `${oneDecimal.format(ms)} ms`
  return `${int.format(Math.round(ms))} ms`
}

export function formatMinutes(minutes: number | null | undefined): string {
  if (!isNum(minutes)) return NO_VALUE
  const total = Math.max(0, Math.round(minutes))
  if (total < 60) return `${int.format(total)} min`
  const hours = Math.floor(total / 60)
  const rest = total % 60
  if (hours < 24) return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`
  const days = Math.floor(hours / 24)
  return `${days} d ${hours % 24} h`
}

export function formatMegabytes(mb: number | null | undefined): string {
  if (!isNum(mb)) return NO_VALUE
  if (mb >= 1024) return `${twoDecimal.format(mb / 1024)} GB`
  return `${int.format(Math.round(mb))} MB`
}

function parse(iso: string | null | undefined): Date | null {
  if (typeof iso !== 'string') return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? new Date(ms) : null
}

/** hh:mm:ss UTC. */
export function formatClock(iso: string | null | undefined): string {
  const date = parse(iso)
  return date === null ? NO_VALUE : clock.format(date)
}

/** hh:mm UTC — axis ticks and compact table cells. */
export function formatClockShort(iso: string | null | undefined): string {
  const date = parse(iso)
  return date === null ? NO_VALUE : clockShort.format(date)
}

export function formatAxisTime(date: Date): string {
  return clockShort.format(date)
}

/** dd/mm UTC — eixo de gráficos por dia. */
export function formatDayMonth(date: Date): string {
  return dayMonth.format(date)
}

/** dd/mm hh:mm UTC — for anything that may be older than today. */
export function formatDayClock(iso: string | null | undefined): string {
  const date = parse(iso)
  return date === null ? NO_VALUE : dayClock.format(date)
}

/**
 * Age of a piece of data, phrased so it cannot be mistaken for a live value.
 * Coarse on purpose above a minute: "há 4 min" is actionable, "há 4 min 12 s"
 * is a number that redraws every second for no reason.
 */
export function formatAge(ms: number | null | undefined): string {
  if (!isNum(ms) || ms < 0) return NO_VALUE
  const seconds = Math.floor(ms / 1000)
  if (seconds < 1) return 'agora'
  if (seconds < 60) return `há ${seconds} s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  return `há ${Math.floor(hours / 24)} d`
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Division that yields 0 instead of NaN/Infinity when the divisor is empty. */
export function ratio(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0
}

export function pctOf(value: number, total: number): number {
  return ratio(value, total) * 100
}
